// ============================================
// FILE: src/components/fashion/ColorSwatchSelector.jsx
// ============================================
//
// Renders as a thumbnail row when the colourway has a photo of its own — a
// shopper picks a colour off the garment, not off an abstract dot — and falls
// back to hex circles when it does not.

import React from 'react';

const ColorSwatchSelector = ({
  option,
  selectedValue = null,
  onChange = () => {},
  isAvailable = () => true,
  thumbnailFor = null,   // (value) => url | null
}) => {
  if (!option || !Array.isArray(option.values) || option.values.length === 0) {
    return null;
  }

  const selected = option.values.find((v) => String(v.value) === String(selectedValue));
  const useThumbnails = typeof thumbnailFor === 'function'
    && option.values.some((v) => thumbnailFor(v));

  return (
    <div className="mb-6">
      <div className="flex items-baseline gap-2 mb-3">
        <span className="fx-label">More {option.label}s</span>
        {selected && (
          <span style={{ fontSize: 14, color: 'var(--fx-ink-2)' }}>{selected.label}</span>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        {option.values.map((value) => {
          const isSelected = String(value.value) === String(selectedValue);
          const available = isAvailable(value.value);
          const thumb = useThumbnails ? thumbnailFor(value) : null;

          if (useThumbnails) {
            return (
              <button
                key={value.id ?? value.value}
                type="button"
                disabled={!available}
                onClick={() => onChange(value.value)}
                title={available ? value.label : `${value.label} — out of stock`}
                aria-pressed={isSelected}
                className="relative overflow-hidden flex-shrink-0"
                style={{
                  width: 56,
                  height: 74,
                  border: isSelected ? '2px solid var(--fx-pink)' : '1px solid var(--fx-line)',
                  borderRadius: 4,
                  opacity: available ? 1 : 0.45,
                  cursor: available ? 'pointer' : 'not-allowed',
                  background: thumb ? undefined : (value.hex || '#e5e5e5'),
                }}
              >
                {thumb && (
                  <img src={thumb} alt={value.label} className="w-full h-full object-cover" loading="lazy" />
                )}
                {!available && (
                  <span
                    className="absolute inset-0"
                    aria-hidden="true"
                    style={{
                      background: 'repeating-linear-gradient(-60deg, transparent, transparent 6px, rgba(255,255,255,0.85) 6px, rgba(255,255,255,0.85) 7px)',
                    }}
                  />
                )}
              </button>
            );
          }

          return (
            <button
              key={value.id ?? value.value}
              type="button"
              disabled={!available}
              onClick={() => onChange(value.value)}
              title={available ? value.label : `${value.label} — out of stock`}
              aria-label={value.label}
              aria-pressed={isSelected}
              className="relative flex-shrink-0"
              style={{
                width: 40,
                height: 40,
                borderRadius: '9999px',
                background: value.image_url ? undefined : (value.hex || '#e5e5e5'),
                backgroundImage: value.image_url ? `url(${value.image_url})` : undefined,
                backgroundSize: 'cover',
                border: '1px solid rgba(0,0,0,0.12)',
                boxShadow: isSelected ? '0 0 0 2px #fff, 0 0 0 4px var(--fx-pink)' : 'none',
                opacity: available ? 1 : 0.4,
                cursor: available ? 'pointer' : 'not-allowed',
              }}
            >
              {!available && (
                <span className="absolute inset-0 flex items-center justify-center" aria-hidden="true">
                  <span
                    className="block w-full"
                    style={{ height: 1, background: 'rgba(0,0,0,0.55)', transform: 'rotate(-45deg)' }}
                  />
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default ColorSwatchSelector;
